import { useTranslation } from "react-i18next";
import { IconX } from "@tabler/icons-react";
import AppModal from "../AppModal";
import { RoomSupportedByChip } from "../../room/RoomSupportedByChip";

/**
 * Post-onboarding confirmation shown on arrival at Discovery when the user
 * signed up via a room invitation. The invited room is already joined at this
 * point (see `OnboardingPage.joinRoom`); this modal invites them to explore it.
 */
export function RoomJoinedModal({
  room,
  onClose,
  onDiscover,
}: {
  room: {
    roomName: string;
    organisationName: string;
    logoUrl: string;
  } | null;
  onClose: () => void;
  onDiscover: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <AppModal
      visible={room !== null}
      overlayStyle={{
        backgroundColor: "rgba(86, 86, 86, 0.3)",
        backdropFilter: "blur(4px)",
      }}
      modalStyle={{
        backgroundColor: "#ffffff",
        color: "#092a23",
        padding: 12,
        gap: 12,
        borderRadius: 12,
        maxWidth: 327,
      }}
    >
      {room !== null && (
        <>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              aria-label={t("Close")}
              className="flex size-7 items-center justify-center rounded-full bg-[#ededed]"
            >
              <IconX className="size-5 text-ds-mint-900" />
            </button>
          </div>

          <div className="flex flex-col items-start gap-3">
            <div className="mx-auto flex size-13 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-[#fbf9f5] p-1.5">
              <img
                src={room.logoUrl}
                alt={room.roomName}
                className="max-h-full max-w-full object-contain object-center"
              />
            </div>

            <h2 className="font-dm-sans text-heading-lg font-semibold text-ds-mint-900">
              {t("roomJoinedModal.title", { room: room.roomName })}
            </h2>

            <RoomSupportedByChip
              organisationName={room.organisationName}
              size="md"
            />

            <p className="font-dm-sans text-ui-body text-[#929292]">
              {t("roomJoinedModal.description")}
            </p>
          </div>

          <button
            type="button"
            onClick={onDiscover}
            className="flex h-14 w-full items-center justify-center rounded-xl border border-ds-border-btn bg-[#135345] px-4 py-2 font-dm-sans text-ui-body font-semibold text-[#fbf9f5]"
          >
            {t("roomJoinedModal.cta")}
          </button>
        </>
      )}
    </AppModal>
  );
}
